import zip from 'lodash/zip';
import map from 'lodash/map';
import each from 'lodash/each';
import tail from 'lodash/tail';
import uniq from 'lodash/uniq';
import times from 'lodash/times';
import sumBy from 'lodash/sumBy';
import sortBy from 'lodash/sortBy';
import fromPairs from 'lodash/fromPairs';

import jStat from 'jstat';
import {
  sampleCorrelation as correlation, mean
} from 'simple-statistics';

import { round4 } from 'utilities/math';
import { action, observable } from 'mobx';
import { getBoxPlotData } from 'calculators/graphicalStatistics/boxPlot';
import { getDotsForProbabilityPlot } from 'calculators/graphicalStatistics/probabilityPlot';

import {
  isParameterNumerical,
  isParameterCategorical
} from 'utilities/parameters';

export const SCATTER_PLOT_TYPES = {
  NONE: 'NONE', MAIN: 'MAIN', RESIDUALS: 'RESIDUALS', NORMAL: 'NORMAL'
};

const linearModel = {
  primaryParameter: null,
  secondaryParameter: null,
  confidenceInterval: 0.05,
  visibleAlphaSlider: false,

  visibleScatterPlotType: SCATTER_PLOT_TYPES.NONE,

  get parametersChoosen () {
    return !!this.primaryParameter && !!this.secondaryParameter;
  },

  get categoricalPrimary () {
    return isParameterCategorical(
      this.primaryParameter
    );
  },

  get numericalPrimary () {
    return isParameterNumerical(
      this.primaryParameter
    );
  },

  get categoricalSecondary () {
    return isParameterCategorical(
      this.secondaryParameter
    );
  },

  get numericalSecondary () {
    return isParameterNumerical(
      this.secondaryParameter
    );
  },

  get performAnovaTest () {
    return this.parametersChoosen && (
      this.categoricalSecondary ||
      this.categoricalPrimary
    );
  },

  get numericalParameter () {
    if (this.numericalPrimary) {
      return this.primaryParameter;
    }

    if (this.numericalSecondary) {
      return this.secondaryParameter;
    }

    return null;
  },

  get categoricalParameter () {
    if (this.categoricalPrimary) {
      return this.primaryParameter;
    }

    if (this.categoricalSecondary) {
      return this.secondaryParameter;
    }

    return null;
  },

  get categoricalParameterLevels () {
    if (this.categoricalParameter) {
      return sortBy(uniq(this.categoricalParameter.values));
    }

    return null;
  },

  get linearRegressionModel () {
    if (this.parametersChoosen) {
      let exog = null;
      let endog = null;

      if (this.performAnovaTest) {
        endog = this.numericalParameter.values;

        const levels = this.categoricalParameterLevels;

        exog = this.categoricalParameter.values.map(level => [
          1, ...times(levels.length - 1, index =>
            levels[index + 1] === level
              ? 1
              : 0
          )
        ]);
      } else {
        endog = this.secondaryParameter.values;
        exog = this.primaryParameter.values.map(v => [1, v]);
      }

      return jStat.models.ols(endog, exog);
    }

    return null;
  },

  get probabilityPlot () {
    if (this.parametersChoosen && this.linearRegressionModel) {
      const { resid } = this.linearRegressionModel;
      return getDotsForProbabilityPlot(resid);
    }
    return null;
  },

  get mainScatterPlotDots () {
    if (this.parametersChoosen) {
      return zip(
        this.primaryParameter.values,
        this.secondaryParameter.values
      );
    }

    return null;
  },

  get residualsScatterPlotDots () {
    if (this.parametersChoosen && this.linearRegressionModel) {
      const residuals = this.linearRegressionModel.resid;

      if (this.performAnovaTest) {
        const { coef } = this.linearRegressionModel;

        times(coef.length - 1, index => {
          coef[index + 1] += coef[0];
        });

        const hash = fromPairs(zip(this.categoricalParameterLevels, coef));
        const dots = zip(this.categoricalParameter.values, residuals);

        times(dots.length, index => {
          dots[index][0] = hash[dots[index][0]];
        });

        return dots;
      }

      return zip(this.primaryParameter.values, residuals);
    }

    return null;
  },

  get boxPlotData () {
    if (this.performAnovaTest) {
      return getBoxPlotData(
        this.numericalParameter.values,
        this.categoricalParameter.values
      );
    }

    return null;
  },

  get primaryParameterMean () {
    return mean(this.primaryParameter.values);
  },

  get confidenceIntervalMsres () {
    return sumBy(this.linearRegressionModel.resid, r => r ** 2) / this.linearRegressionModel.df_resid;
  },

  get confidenceIntervalSxx () {
    return sumBy(this.primaryParameter.values, primaryParameterValue => (primaryParameterValue - this.primaryParameterMean) ** 2);
  },

  get confidenceIntervalDots () {
    if (this.parametersChoosen && !this.performAnovaTest) {
      const tval = jStat.studentt.inv(1 - (this.confidenceInterval / 2), this.linearRegressionModel.df_resid);

      const sxx = this.confidenceIntervalSxx;
      const msres = this.confidenceIntervalMsres;

      const lwrs = [];
      const uprs = [];

      each(this.linearRegressionModel.predict, (predictedValue, index) => {
        const primaryParameterValue = this.primaryParameter.values[index];

        const offset = tval * Math.sqrt(msres * ((1 / this.linearRegressionModel.nobs) + ((primaryParameterValue - this.primaryParameterMean) ** 2 / sxx)));

        lwrs.push({ x: primaryParameterValue, y: predictedValue - offset });
        uprs.push({ x: primaryParameterValue, y: predictedValue + offset });
      });

      return {
        lwrs: sortBy(lwrs, 'x'),
        uprs: sortBy(uprs, 'x')
      };
    }

    return null;
  },

  get summaryFormula () {
    if (this.linearRegressionModel) {
      let formula = '';
      const model = this.linearRegressionModel;
      const interceptCoef = round4(model.coef[0]);

      if (this.performAnovaTest) {
        const levels = this.categoricalParameterLevels;
        formula += `${this.numericalParameter.name} = `;

        formula += map(tail(levels), (level, index) =>
          `(${level} x ${round4(model.coef[index + 1])})`
        ).join(' + ');

      } else {
        formula += `${this.secondaryParameter.name} = `;
        formula += `(${this.primaryParameter.name} x ${round4(model.coef[1])})`;
      }

      formula += ` ${interceptCoef >= 0 ? '+' : '-'} ${Math.abs(interceptCoef)}`;

      return formula;
    }

    return null;
  },

  get correlationCoefficient () {
    if (this.numericalPrimary) {
      return (this.primaryParameter.values.length > 1 && this.secondaryParameter.values.length > 1) ? round4(
        correlation(
          this.primaryParameter.values,
          this.secondaryParameter.values
        )
      ) : 'N/A';
    }
    return null;
  },

  get modelSummary () {
    if (this.linearRegressionModel) {
      const model = this.linearRegressionModel;
      return {
        // sample standard deviation
        rse: Math.sqrt(jStat.sumsqerr(model.resid) / (model.resid.length - 2)),
        f: model.f.F_statistic,
        dof: model.df_resid,
        p: model.f.pvalue,
        r2: model.R2,
        correlation: this.correlationCoefficient,
        formula: this.summaryFormula
      };
    }

    return null;
  },

  get coefficientSummary () {
    if (this.linearRegressionModel) {
      const model = this.linearRegressionModel;

      if (this.performAnovaTest) {
        const levels = this.categoricalParameterLevels;

        return [
          {
            name: `Intercept (${this.categoricalParameter.name} - Level ${levels[0]})`,
            tScore: model.t.t[0],
            pValue: model.t.p[0],
            stderr: model.t.se[0],
            estimate: model.coef[0]
          },

          ...map(tail(levels), (level, index) => ({
            name: `${this.categoricalParameter.name} - Level ${level}`,

            pValue: model.t.p[index + 1],
            tScore: model.t.t[index + 1],
            stderr: model.t.se[index + 1],
            estimate: model.coef[index + 1]
          }))
        ];
      } else {
        return [
          {
            name: 'Intercept',
            tScore: model.t.t[0],
            pValue: model.t.p[0],
            stderr: model.t.se[0],
            estimate: model.coef[0]
          },
          {
            name: this.primaryParameter.name,
            estimate: model.coef[1],
            stderr: model.t.se[1],
            tScore: model.t.t[1],
            pValue: model.t.p[1]
          }
        ];
      }
    }

    return null;
  },

  get areConfidenceIntervalsControlsVisible () {
    return (
      this.visibleScatterPlotType === SCATTER_PLOT_TYPES.MAIN &&
      this.visibleAlphaSlider
    );
  },

  setVisibleSlider: action(
    'SET VISIBLE SLIDER',
    function (isVisibleSlider) {
      this.visibleAlphaSlider = isVisibleSlider;
    }
  ),

  setPrimaryAndSecondary: action(
    'SET PRIMARY AND SECONDARY',

    function (primaryParameter, secondaryParameter) {
      this.primaryParameter = primaryParameter;
      this.secondaryParameter = secondaryParameter;
    }
  ),

  setPrimaryAndResetSecondary: action(
    'SET PRIMARY AND RESET SECONDARY',

    function (primaryParameter) {
      this.primaryParameter = primaryParameter;
      this.secondaryParameter = null;
      this.confidenceInterval = 0.05;
      this.visibleAlphaSlider = false;
    }
  ),

  resetPrimaryAndSecondary: action(
    'RESET PRIMARY AND SECONDARY',

    function () {
      this.primaryParameter = null;
      this.secondaryParameter = null;
      this.confidenceInterval = 0.05;
      this.visibleAlphaSlider = false;
    }
  )
};

export const createLinearModelObservable = () => observable(linearModel);
